import { Toast } from 'antd-mobile';
import { getUrlParam, isInWxFromUrlQuery, safeLocalStorageSet } from '../../../utils/common';
import { isWxMiniProgramWebview } from '../../../utils/EnvUtils';
import { JsBridge } from '../../../utils/JsBridge';
import { navigateBackFromEmbedded } from '../../../utils/navigateBackFromEmbedded';

/**
 * 当前 H5 是否运行在测试/调试域名（与 `index.tsx` 的 xsmtest、VIP 页的 testy6、局域网调试一致）。
 *
 * @returns 为 true 时可在控制台输出完整访问链接等调试信息
 */
function isXsmH5TestHost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const host = window.location.hostname;
  return (
    host === 'testy6.sduck.net' ||
    host === 'xsmtest.sduck.net' ||
    host.endsWith('.xsmtest.sduck.net') ||
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.startsWith('192.168.')
  );
}

/** 文件导入页导航标题 */
export const XSM_FILE_IMPORT_PAGE_TITLE = '文件导入';

/** 拍照导入页导航标题 */
export const XSM_PHOTO_IMPORT_PAGE_TITLE = '拍照导入';

/** 微信导入页导航标题 */
export const XSM_WECHAT_IMPORT_PAGE_TITLE = '微信导入';

/**
 * 根据 `importChannel` 解析文件导入中间页标题。
 *
 * @param importChannel URL 参数 `importChannel`
 */
export function resolveXsmFileImportPageTitle(importChannel: string): string {
  const ch = importChannel.trim();
  if (ch === 'wechat') {
    return XSM_WECHAT_IMPORT_PAGE_TITLE;
  }
  if (ch === 'photo') {
    return XSM_PHOTO_IMPORT_PAGE_TITLE;
  }
  return XSM_FILE_IMPORT_PAGE_TITLE;
}

/** 试卷还原结果页导航标题 */
export const XSM_PAPER_RESTORE_RESULT_PAGE_TITLE = '试卷还原结果';

/** 内嵌页路径 → 默认导航标题（首屏脚本与路由入口也会用到） */
const XSM_EMBEDDED_PATH_TITLES: Readonly<Record<string, string>> = {
  '/xsm/file_import': XSM_FILE_IMPORT_PAGE_TITLE,
  '/xsm/file_import/paper_restore_result': XSM_PAPER_RESTORE_RESULT_PAGE_TITLE,
  '/xsm/exam_paper_export': '导出试卷',
  '/form_teacher': '老师申请免费会员',
  '/beta_apply': '内测功能申请',
  '/activity_submit_list': '活动列表',
  '/activity-submit-list': '活动列表',
};

/**
 * 立即写入内嵌页标题（document.title + App JsBridge），供首屏与懒加载前调用。
 *
 * @param pageTitle 页面标题文案
 */
export function applyXsmEmbeddedPageTitle(pageTitle: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  const title = pageTitle.trim();
  if (!title) {
    return;
  }
  document.title = title;
  JsBridge.setNavigationBar(
    JSON.stringify({
      visible: false,
      statusBarColor: '#ffffff',
      title,
    }),
  );
}

/**
 * 按当前 URL 路径写入内嵌页标题（在 React 懒加载前调用，避免 web-view 同步到 index.html 默认 title）。
 */
export function applyXsmEmbeddedPageTitleFromPath(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const path = window.location.pathname || '';
  const title = XSM_EMBEDDED_PATH_TITLES[path];
  if (title) {
    applyXsmEmbeddedPageTitle(title);
  }
}

/** 小程序 web-view 内短时守护 document.title 的定时器 id（浏览器环境为 number） */
let wxNavigationTitleGuardTimer: number | null = null;

/**
 * 写入 document.title 并同步 `<title>` 节点（微信 web-view 依赖后者触发导航栏更新）。
 *
 * @param title 目标标题
 */
function writeDocumentTitle(title: string): void {
  document.title = title;
  const titleEl = document.querySelector('title');
  if (titleEl && titleEl.textContent !== title) {
    titleEl.textContent = title;
  }
}

/**
 * 小程序 web-view 内短时重复写入标题，促导航栏跟随 H5 内伪页面切换。
 *
 * @param pageTitle 目标标题
 */
function pulseXsmWxNavigationTitleGuard(pageTitle: string): void {
  if (typeof window === 'undefined' || !isInWxFromUrlQuery()) {
    return;
  }
  const title = pageTitle.trim();
  if (!title) {
    return;
  }
  if (wxNavigationTitleGuardTimer !== null) {
    window.clearInterval(wxNavigationTitleGuardTimer);
    wxNavigationTitleGuardTimer = null;
  }
  let ticks = 0;
  wxNavigationTitleGuardTimer = window.setInterval(() => {
    writeDocumentTitle(title);
    ticks += 1;
    if (ticks >= 4) {
      if (wxNavigationTitleGuardTimer !== null) {
        window.clearInterval(wxNavigationTitleGuardTimer);
        wxNavigationTitleGuardTimer = null;
      }
    }
  }, 250);
}

/**
 * 小程序内短时守护 document.title，防止首屏默认 title 再次被 web-view 同步上去。
 *
 * @param pageTitle 目标标题
 * @returns 清理定时器
 */
export function startXsmWxNavigationTitleGuard(pageTitle: string): () => void {
  if (typeof window === 'undefined' || !isInWxFromUrlQuery()) {
    return () => {};
  }
  const title = pageTitle.trim();
  if (!title) {
    return () => {};
  }
  writeDocumentTitle(title);
  pulseXsmWxNavigationTitleGuard(title);
  return () => {
    if (wxNavigationTitleGuardTimer !== null) {
      window.clearInterval(wxNavigationTitleGuardTimer);
      wxNavigationTitleGuardTimer = null;
    }
  };
}

/**
 * 通知小程序 / App 更新导航栏标题：改 document.title（web-view 会同步）并 postMessage（若可用）。
 *
 * @param pageTitle 页面标题文案
 */
export function notifyXsmEmbeddedNavigationTitle(pageTitle: string): void {
  const title = pageTitle.trim();
  if (!title) {
    return;
  }
  writeDocumentTitle(title);
  if (typeof window !== 'undefined' && isInWxFromUrlQuery()) {
    window.requestAnimationFrame(() => {
      writeDocumentTitle(title);
    });
    pulseXsmWxNavigationTitleGuard(title);
    const wxGlobal = (window as Window & { wx?: { miniProgram?: { postMessage?: (arg: unknown) => void } } })
      .wx;
    wxGlobal?.miniProgram?.postMessage?.({
      data: { type: 'title', title },
    });
    return;
  }
  JsBridge.setNavigationBar(
    JSON.stringify({
      visible: false,
      statusBarColor: '#ffffff',
      title,
    }),
  );
}

/**
 * 小四门 H5 内嵌页鉴权：仅从 URL query 读取 `token`、`appId` 写入 localStorage，供 axios 拦截器使用。
 * 不使用 ticket 换票；token 由小程序 `buildAuthedWebviewUrl` 直接拼入 URL。
 *
 * @param pageTitle 浏览器标题与 JsBridge 导航栏文案
 * @returns 清理函数（恢复 viewport / overscroll）
 */
export function initXsmWebviewAuth(pageTitle: string): () => void {
  if (isXsmH5TestHost()) {
    console.log(`[小四门 H5][${pageTitle}] 完整链接:`, window.location.href);
  }

  const viewportMeta = document.querySelector('meta[name="viewport"]');
  if (viewportMeta) {
    viewportMeta.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
    );
  }
  document.body.style.overscrollBehavior = 'none';
  notifyXsmEmbeddedNavigationTitle(pageTitle);
  const stopTitleGuard = startXsmWxNavigationTitleGuard(pageTitle);

  const urlAppId = getUrlParam('appId').trim();
  const appId = urlAppId || 'uro25b6xwubbvfpi';
  if (appId) {
    safeLocalStorageSet('ticket_appId', appId);
  }

  const tokenFromUrl = getUrlParam('token').trim();
  if (tokenFromUrl) {
    safeLocalStorageSet('ticket_token', tokenFromUrl);
  } else if (isInWxFromUrlQuery()) {
    Toast.show({
      content: '登录信息缺失，请返回小程序重新打开',
      position: 'center',
    });
  }

  return () => {
    stopTitleGuard();
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
      );
    }
    document.body.style.overscrollBehavior = '';
  };
}

/**
 * 从小程序 web-view 内嵌页返回：优先 `wx.miniProgram.navigateBack`，否则走 SPA `navigate(-1)` 或 `history.back`。
 */
export function exitXsmEmbeddedPage(fallbackNavigateBack?: () => void): void {
  if (isWxMiniProgramWebview()) {
    navigateBackFromEmbedded();
    return;
  }
  if (fallbackNavigateBack) {
    fallbackNavigateBack();
    return;
  }
  navigateBackFromEmbedded();
}

/**
 * 题目/知识点保存成功后：通知小程序刷新试卷详情，并关闭 web-view 编辑页。
 */
export function finishXsmQuestionSaveAndExit(fallbackNavigateBack?: () => void): void {
  if (isWxMiniProgramWebview()) {
    const wxGlobal = (
      window as Window & { wx?: { miniProgram?: { postMessage?: (arg: unknown) => void } } }
    ).wx;
    try {
      wxGlobal?.miniProgram?.postMessage?.({
        data: { action: 'question_save_success' },
      });
    } catch {
      /* ignore */
    }
    setTimeout(() => {
      navigateBackFromEmbedded();
    }, 400);
    return;
  }
  if (fallbackNavigateBack) {
    fallbackNavigateBack();
    return;
  }
  navigateBackFromEmbedded();
}

/**
 * 是否隐藏 H5 顶栏（小程序 web-view 使用原生导航栏）。
 * `hideHeader=1` 或 `inWx=1` 时返回 true。
 */
export function shouldHideXsmEmbeddedTopNav(): boolean {
  if (isInWxFromUrlQuery()) {
    return true;
  }
  const h = getUrlParam('hideHeader').trim().toLowerCase();
  if (h === '1' || h === 'true') {
    return true;
  }
  /** 兜底：web-view 地址中 inWx / hideHeader 被写成 %26 拼接时 */
  const href = typeof window !== 'undefined' ? window.location.href.toLowerCase() : '';
  if (
    /(?:^|[?&%26])inwx=(?:1|true)(?:&|$|%26)/i.test(href) ||
    /(?:^|[?&%26])hideheader=(?:1|true)(?:&|$|%26)/i.test(href)
  ) {
    return true;
  }
  return false;
}
